import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Flame, CheckCircle2, Dumbbell, Clock, MapPin, Smile, Zap, CalendarDays, Search, Loader2, Play } from "lucide-react";
import { format, startOfMonth, startOfWeek, addDays, addMonths, isSameMonth, isSameDay, parseISO } from "date-fns";
import { id as idLocale, enUS } from "date-fns/locale";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { SectionHeader } from "@/components/Shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const iso = (d) => format(d, "yyyy-MM-dd");

const StatPill = ({ icon: Icon, value, label, cls, dim }) => (
  <div className={cn("rounded-2xl border bg-card p-4 transition-opacity", dim && "opacity-40")}>
    <span className={cn("grid h-9 w-9 place-items-center rounded-xl", cls)}><Icon className="h-4.5 w-4.5" /></span>
    <div className="mt-2 font-display text-2xl font-bold tabular-nums">{value}</div>
    <div className="text-xs text-muted-foreground">{label}</div>
  </div>
);

export default function History() {
  const { t, lang } = useI18n();
  const id = lang === "id";
  const locale = id ? idLocale : enUS;
  const [monthOffset, setMonthOffset] = useState(0);
  const [selected, setSelected] = useState(iso(new Date()));
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openSession, setOpenSession] = useState(null);
  const q = search.trim().toLowerCase();

  const { data } = useQuery({ queryKey: ["activity-history"], queryFn: async () => (await api.get("/activity/history")).data });
  const byDate = useMemo(() => {
    const m = {};
    (data?.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [data]);

  const sessionMatchesQ = (s) => !q || (s.exercises || []).some((e) => e.toLowerCase().includes(q)) || (s.name || "").toLowerCase().includes(q);
  const dayHasWorkout = (day) => (day?.sessions || []).some(sessionMatchesQ);
  // Does a day pass the active filter + search?
  const dayMatches = (day) => {
    if (!day) return false;
    const hasWo = dayHasWorkout(day);
    if (q) return hasWo; // search always targets exercises/workouts
    if (filter === "workout") return (day.sessions?.length || 0) > 0;
    if (filter === "checkin") return !!day.checkin;
    return !!day.checkin || (day.sessions?.length || 0) > 0;
  };
  const filterActive = q || filter !== "all";

  const month = addMonths(new Date(), monthOffset);
  const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const cells = [...Array(42)].map((_, i) => addDays(gridStart, i));
  const weekdays = [...Array(7)].map((_, i) => format(addDays(gridStart, i), "EEEEE", { locale }));

  const monthDays = (data?.days || []).filter((d) => isSameMonth(parseISO(d.date), month));
  const monthCals = monthDays.reduce((a, d) => a + (q
    ? (d.sessions || []).filter(sessionMatchesQ).reduce((x, s) => x + (s.calories || 0), 0)
    : (d.calories_burned || 0)), 0);
  const monthSessions = monthDays.reduce((a, d) => a + (q
    ? (d.sessions || []).filter(sessionMatchesQ).length
    : (d.sessions?.length || 0)), 0);
  const monthCheckins = monthDays.filter((d) => d.checkin).length;

  const sel = byDate[selected];

  return (
    <div className="mx-auto max-w-3xl space-y-5 pb-24">
      <SectionHeader title={t("history")} subtitle={t("histSubtitle")} />

      <div className="grid grid-cols-3 gap-3">
        <StatPill icon={Flame} value={monthCals.toLocaleString()} label={`${t("histTotalCal")} · ${t("histThisMonth")}`} cls="bg-orange-500/15 text-orange-500" dim={filter === "checkin"} />
        <StatPill icon={Dumbbell} value={monthSessions} label={`${t("histTotalSessions")} · ${t("histThisMonth")}`} cls="bg-primary/15 text-primary" dim={filter === "checkin"} />
        <StatPill icon={CheckCircle2} value={monthCheckins} label={`${t("histTotalCheckins")} · ${t("histThisMonth")}`} cls="bg-emerald-500/15 text-emerald-500" dim={filter === "workout" || !!q} />
      </div>

      {/* Filter + search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex rounded-xl border bg-card p-1" data-testid="hist-filter">
          {[["all", t("histFilterAll")], ["workout", t("histFilterWorkout")], ["checkin", t("histFilterCheckin")]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`hist-filter-${k}`}
              className={cn("flex-1 whitespace-nowrap rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors",
                filter === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("histSearchPlaceholder")}
            className="h-10 rounded-xl pl-9" data-testid="hist-search" />
        </div>
      </div>

      {/* Month calendar */}
      <div className="rounded-3xl border bg-gradient-to-b from-card to-card/60 p-4 sm:p-5">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold capitalize">{format(month, "MMMM yyyy", { locale })}</h3>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setMonthOffset(monthOffset - 1)} data-testid="hist-prev" className="grid h-9 w-9 place-items-center rounded-full border transition-colors hover:bg-accent"><ChevronLeft className="h-4 w-4" /></button>
            <button onClick={() => setMonthOffset(0)} data-testid="hist-today" className="rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors hover:bg-accent">{id ? "Ini" : "Now"}</button>
            <button onClick={() => setMonthOffset(monthOffset + 1)} data-testid="hist-next" className="grid h-9 w-9 place-items-center rounded-full border transition-colors hover:bg-accent"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="mb-2 grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          {weekdays.map((w, i) => <div key={i}>{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {cells.map((d) => {
            const key = iso(d);
            const day = byDate[key];
            const inMonth = isSameMonth(d, month);
            const isToday = isSameDay(d, new Date());
            const isSel = key === selected;
            const matched = dayMatches(day);
            const hasCheckin = !!day?.checkin && filter !== "workout" && !q;
            const hasWorkout = (day?.sessions?.length || 0) > 0 && filter !== "checkin" && (!q || dayHasWorkout(day));
            const dim = (!inMonth) || (filterActive && !matched);
            return (
              <button key={key} onClick={() => setSelected(key)} data-testid={`hist-day-${key}`}
                className={cn(
                  "relative flex aspect-square items-center justify-center rounded-2xl text-sm font-semibold tabular-nums transition-all duration-200",
                  dim && "opacity-25",
                  isSel
                    ? "scale-[1.04] bg-gradient-to-br from-primary to-primary/75 text-primary-foreground shadow-[0_6px_20px_hsl(var(--primary)/0.4)]"
                    : hasCheckin && hasWorkout ? "bg-emerald-500/15 text-emerald-500 ring-1 ring-inset ring-emerald-500/30"
                      : hasCheckin ? "bg-emerald-500/12 text-emerald-500"
                        : hasWorkout ? "bg-orange-500/12 text-orange-500"
                          : isToday ? "text-primary ring-1 ring-inset ring-primary/50"
                            : "text-foreground/80 hover:bg-accent",
                )}>
                {format(d, "d")}
                {hasWorkout && !isSel && (
                  <span className="absolute right-1 top-1 grid h-3 w-3 place-items-center rounded-full bg-orange-500/90">
                    <Flame className="h-2 w-2 text-white" />
                  </span>
                )}
                {isToday && !isSel && <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-4 flex flex-wrap items-center gap-4 border-t pt-3 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500/60" /> {t("histCheckedIn")}</span>
          <span className="flex items-center gap-1.5"><span className="grid h-3 w-3 place-items-center rounded-full bg-orange-500/90"><Flame className="h-2 w-2 text-white" /></span> {t("histWorkouts")}</span>
        </div>
      </div>

      {/* Selected day detail */}
      <div className="rounded-3xl border bg-card p-5" data-testid="hist-detail">
        <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
          <CalendarDays className="h-4 w-4 text-primary" />
          <span className="capitalize">{format(parseISO(selected), "EEEE, d MMMM yyyy", { locale })}</span>
        </div>

        {!sel ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("histNoActivity")}</p>
        ) : filterActive && !dayMatches(sel) ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("histNoMatch")}</p>
        ) : (() => {
          const showCheckinCard = !!sel.checkin && filter !== "workout" && !q;
          const showCaloriesCard = filter !== "checkin";
          const shownSessions = filter === "checkin" ? [] : (q ? sel.sessions.filter(sessionMatchesQ) : sel.sessions);
          return (
          <div className="space-y-4">
            {/* Check-in + calories row */}
            {(showCheckinCard || showCaloriesCard) && (
            <div className={cn("grid gap-3", showCheckinCard && showCaloriesCard ? "grid-cols-2" : "grid-cols-1")}>
              {showCheckinCard && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {t("histCheckedIn")}
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {sel.checkin.mood != null && <span className="flex items-center gap-1"><Smile className="h-3.5 w-3.5" /> {sel.checkin.mood}/5</span>}
                  {sel.checkin.energy != null && <span className="flex items-center gap-1"><Zap className="h-3.5 w-3.5" /> {sel.checkin.energy}/5</span>}
                  {sel.checkin.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {sel.checkin.location}</span>}
                </div>
              </div>
              )}
              {showCaloriesCard && (
              <div className="rounded-2xl border border-orange-500/25 bg-orange-500/5 p-4">
                <div className="flex items-center gap-2 text-sm font-medium"><Flame className="h-4 w-4 text-orange-500" /> {t("histCaloriesBurned")}</div>
                <div className="mt-1 font-display text-2xl font-bold tabular-nums text-orange-500">
                  {(q ? shownSessions.reduce((a, s) => a + (s.calories || 0), 0) : (sel.calories_burned || 0)).toLocaleString()}<span className="ml-1 text-xs font-normal text-muted-foreground">kkal</span>
                </div>
                <div className="text-[10px] text-muted-foreground">{t("histEstimate")}</div>
              </div>
              )}
            </div>
            )}

            {/* Workouts */}
            {shownSessions.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Dumbbell className="h-4 w-4 text-primary" /> {t("histWorkouts")}</div>
                <div className="space-y-2.5">
                  {shownSessions.map((s, i) => (
                    <button key={i} onClick={() => s.id && setOpenSession(s.id)} data-testid={`hist-session-${i}`}
                      className="w-full rounded-2xl border bg-muted/30 p-4 text-left transition-colors hover:border-primary/40 hover:bg-accent">
                      <div className="flex items-center justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-1.5 truncate font-display font-semibold">
                          {s.name}
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </span>
                        <span className="flex shrink-0 items-center gap-1 rounded-full bg-orange-500/15 px-2.5 py-1 text-xs font-semibold text-orange-500">
                          <Flame className="h-3.5 w-3.5" /> {s.calories} kkal
                        </span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {s.duration_min > 0 && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {s.duration_min} {t("histMinutes")}</span>}
                        <span className="flex items-center gap-1"><Dumbbell className="h-3.5 w-3.5" /> {s.sets} {t("histSets")}</span>
                        {s.volume > 0 && <span>{(s.volume).toLocaleString()} kg</span>}
                      </div>
                      {s.exercises?.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {s.exercises.map((ex, j) => {
                            const hit = q && ex.toLowerCase().includes(q);
                            return (
                              <span key={j} className={cn("rounded-full px-2.5 py-1 text-xs font-medium",
                                hit ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary")}>{ex}</span>
                            );
                          })}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showCheckinCard && sel.checkin?.notes && (
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("notes")}</div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{sel.checkin.notes}</p>
              </div>
            )}
          </div>
          );
        })()}
      </div>

      <SessionDetailDialog sessionId={openSession} open={!!openSession} onClose={() => setOpenSession(null)} />
    </div>
  );
}

function SessionDetailDialog({ sessionId, open, onClose }) {
  const { t, lang } = useI18n();
  const id = lang === "id";
  const locale = id ? idLocale : enUS;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [repeating, setRepeating] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["session-detail", sessionId],
    queryFn: async () => (await api.get(`/sessions/${sessionId}`)).data,
    enabled: open && !!sessionId,
  });
  const summ = data?.summary || {};
  const logs = data?.logs || [];

  const repeat = async () => {
    setRepeating(true);
    try {
      const newLogs = logs.map((lg) => ({
        exercise_id: lg.exercise_id,
        exercise_name: lg.exercise_name,
        type: lg.type,
        sets: (lg.sets || [{}]).map(() => ({ weight: "", reps: "", warmup: false })),
      }));
      const { data: ns } = await api.post("/sessions", { name: data?.name || t("histWorkouts") });
      await api.put(`/sessions/${ns.id}`, { logs: newLogs });
      await qc.invalidateQueries({ queryKey: ["active-session"] });
      toast.success(t("workoutRepeated"));
      onClose();
      navigate("/train");
    } catch { toast.error("Error"); } finally { setRepeating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto no-scrollbar" data-testid="session-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{data?.name || t("histWorkouts")}</DialogTitle>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            {data.date && <p className="-mt-2 text-sm capitalize text-muted-foreground">{format(parseISO(data.date), "EEEE, d MMMM yyyy", { locale })}</p>}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-xl bg-muted/40 p-3">
                <div className="font-display text-lg font-bold tabular-nums">{summ.duration_min || 0}</div>
                <div className="text-[11px] text-muted-foreground">{t("histMinutes")}</div>
              </div>
              <div className="rounded-xl bg-muted/40 p-3">
                <div className="font-display text-lg font-bold tabular-nums">{(summ.volume || 0).toLocaleString()}</div>
                <div className="text-[11px] text-muted-foreground">kg</div>
              </div>
              <div className="rounded-xl bg-muted/40 p-3">
                <div className="font-display text-lg font-bold tabular-nums">{summ.total_sets || 0}</div>
                <div className="text-[11px] text-muted-foreground">{t("histSets")}</div>
              </div>
            </div>

            <div className="space-y-3">
              {logs.map((lg, i) => {
                const sets = (lg.sets || []).filter((s) => s.weight || s.reps);
                return (
                  <div key={i} className="rounded-2xl border p-4" data-testid={`session-ex-${i}`}>
                    <div className="mb-2 flex items-center gap-2 font-display font-semibold">
                      <Dumbbell className="h-4 w-4 text-primary" /> {lg.exercise_name}
                    </div>
                    <div className="space-y-1">
                      {sets.length ? sets.map((s, j) => (
                        <div key={j} className="flex items-center justify-between rounded-lg bg-muted/30 px-3 py-1.5 text-sm">
                          <span className="flex items-center gap-2 text-muted-foreground">
                            <span className="grid h-5 w-5 place-items-center rounded-full bg-primary/15 text-[11px] font-bold text-primary">{j + 1}</span>
                            {s.warmup && <span className="rounded bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-500">{id ? "Pemanasan" : "Warmup"}</span>}
                          </span>
                          <span className="font-medium tabular-nums">{s.weight || 0} kg × {s.reps || 0}</span>
                        </div>
                      )) : <p className="text-xs text-muted-foreground">{id ? "Tidak ada set tercatat." : "No sets logged."}</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            {data.notes && (
              <div className="rounded-2xl border bg-muted/30 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("notes")}</div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{data.notes}</p>
              </div>
            )}

            {logs.length > 0 && (
              <Button onClick={repeat} disabled={repeating} size="lg" className="w-full gap-2" data-testid="history-repeat-workout-btn">
                {repeating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {t("repeatWorkout")}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
